import * as React from 'react';
import isEqual from 'react-fast-compare';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { isUiMobile } from '@/redux/modules/browser';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLocale } from '@/redux/modules/intl';
import { useNewBiddingConsoleExperiment } from '@/utils/amplitude/experiments/hooks/useNewBiddingConsole.experiment';
import { popupCenter } from '@/utils/popupCenter';
import { getLiveBiddingUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    children: React.ReactNode;
    onNavigate?: () => void;
};

const ALT_WIDTH = 1648;
const ALT_HEIGHT = 1248;
const WIDTH = 1248;
const HEIGHT = 800;

/**
 * Turn any react component into a button that opens the bidding console in a new window.
 */
const BidConsoleOpener = ({ catalogId = 0, children = '', onNavigate }: Props) => {
    const hasNewBiddingConsole = useNewBiddingConsoleExperiment();

    const navigate = useNavigate();
    const locale = useAppSelector(getLocale);
    const mobile = useAppSelector(isUiMobile);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));

    const localePath = locale === 'en-gb' ? `/${locale}` : '';

    const openBidConsole = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            onNavigate?.();
            /**
             * Enable either mobile devices or holding down the shift key to open the console in the same window.
             */
            if (Boolean(mobile) || event.shiftKey) {
                navigate(getLiveBiddingUrl(catalogId));
            } else {
                popupCenter({
                    height: hasNewBiddingConsole ? ALT_HEIGHT : HEIGHT,
                    title,
                    url: localePath + getLiveBiddingUrl(catalogId),
                    width: hasNewBiddingConsole ? ALT_WIDTH : WIDTH,
                });
            }
        },
        [onNavigate, mobile, navigate, catalogId, hasNewBiddingConsole, title, localePath]
    );

    if (React.isValidElement(children)) {
        return React.cloneElement(React.Children.only(children), {
            /* @ts-expect-error `children` could be anything */
            onClick: openBidConsole,
        });
    }

    return null;
};

export default React.memo<Props>(BidConsoleOpener, isEqual);
